import React, {
  useEffect,
  useState
} from 'react';

import {
  AnimatePresence,
  motion
} from 'motion/react';

import {
  ArrowLeft,
  Folder,
  FolderOpen,
  HardDrive,
  Loader2,
  RefreshCw,
  ShieldCheck,
  X,
  AlertTriangle
} from 'lucide-react';

import {
  BrowseDirectoryEntry,
  BrowseDirectoryResultPayload
} from '@kaizen/shared';

import {
  useRealtime
} from '../../contexts/RealtimeContext';

import {
  kaizenOverlayTransition,
  kaizenSheetTransition,
  kaizenStateTransition
} from '../../lib/kaizenMotion';


interface FolderBrowserModalProps {
  isOpen: boolean;

  onClose:
    () => void;

  onSelect:
    (
      path:
        string
    ) => void;
}


export const FolderBrowserModal:
React.FC<FolderBrowserModalProps> = ({
  isOpen,
  onClose,
  onSelect
}) => {
  const {
    browseDirectory,
    device,
    deviceStatus
  } =
    useRealtime();

  const [
    result,
    setResult
  ] =
    useState<
      BrowseDirectoryResultPayload |
      null
    >(
      null
    );

  const [
    loading,
    setLoading
  ] =
    useState(false);

  const [
    error,
    setError
  ] =
    useState<
      string |
      null
    >(
      null
    );


  const load =
    async (
      path?:
        string
    ) => {
      setLoading(
        true
      );

      setError(
        null
      );

      try {
        const next =
          await browseDirectory(
            path
          );

        setResult(
          next
        );

        if (
          !next.success
        ) {
          setError(
            next.error ||
            'Unable to browse this directory.'
          );
        }
      }
      catch (
        err:
          any
      ) {
        setError(
          err?.message ||
          'Unable to contact the laptop Agent.'
        );
      }
      finally {
        setLoading(
          false
        );
      }
    };


  useEffect(
    () => {
      if (
        !isOpen
      ) {
        return;
      }

      setResult(
        null
      );

      setError(
        null
      );

      void load();
    },
    [
      isOpen
    ]
  );


  const entries:
    BrowseDirectoryEntry[] =
    result?.entries ||
    [];

  const currentPath =
    result?.currentPath;

  const parentPath =
    result?.parentPath;

  const showingRoots =
    !currentPath;


  const handleBack = () => {
    if (
      loading
    ) {
      return;
    }

    if (
      parentPath
    ) {
      void load(
        parentPath
      );

      return;
    }

    if (
      currentPath
    ) {
      void load();

      return;
    }

    onClose();
  };


  return (
    <AnimatePresence initial={false}>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={kaizenOverlayTransition}
          className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-slate-950/40 backdrop-blur-sm sm:p-4"
        >
          <motion.div
            initial={{
              opacity: 0,
              y: 18,
              scale: 0.994
            }}
            animate={{
              opacity: 1,
              y: 0,
              scale: 1
            }}
            exit={{
              opacity: 0,
              y: 14,
              scale: 0.996
            }}
            transition={kaizenSheetTransition}
            className="w-full sm:max-w-xl max-h-[90dvh] bg-white rounded-t-2xl sm:rounded-xl border border-slate-200 shadow-2xl overflow-hidden flex flex-col"
          >
        <div className="shrink-0 px-4 sm:px-5 pt-3 sm:pt-4 pb-3 border-b border-slate-100 bg-white/95 backdrop-blur-xl">
          <div className="w-10 h-1 rounded-full bg-slate-200 mx-auto mb-3 sm:hidden" />

          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-xl bg-indigo-50 border border-indigo-100 flex items-center justify-center text-indigo-600 shrink-0">
                <FolderOpen className="w-5 h-5" />
              </div>

              <div className="min-w-0">
                <h3 className="text-sm font-bold text-slate-900">
                  Browse Personal Laptop
                </h3>

                <p className="text-[11px] text-slate-500 truncate">
                  {device?.name || 'Windows Host'}
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="w-9 h-9 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center active:bg-slate-200"
              aria-label="Close folder browser"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>


        <div className="shrink-0 px-4 sm:px-5 py-3 border-b border-slate-100 bg-slate-50/70">
          <div className="flex items-center gap-2 min-w-0">
            <button
              type="button"
              onClick={handleBack}
              disabled={loading}
              className="w-9 h-9 shrink-0 rounded-lg border border-slate-200 bg-white text-slate-600 flex items-center justify-center disabled:opacity-40"
              title="Back"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>

            <div className="min-w-0 flex-1 h-9 px-3 rounded-lg border border-slate-200 bg-white flex items-center">
              <HardDrive className="w-3.5 h-3.5 text-slate-400 shrink-0 mr-2" />

              <motion.span
                key={
                  showingRoots
                    ? 'this-pc'
                    : currentPath
                }
                initial={{
                  opacity: 0,
                  x: 5
                }}
                animate={{
                  opacity: 1,
                  x: 0
                }}
                transition={
                  kaizenStateTransition
                }
                className="text-[11px] font-mono text-slate-700 truncate"
              >
                {showingRoots
                  ? 'This PC'
                  : currentPath}
              </motion.span>
            </div>

            <button
              type="button"
              disabled={loading}
              onClick={() =>
                void load(
                  currentPath
                )
              }
              className="w-9 h-9 shrink-0 rounded-lg border border-slate-200 bg-white text-slate-600 flex items-center justify-center disabled:opacity-40"
              title="Refresh"
            >
              <RefreshCw
                className={`w-4 h-4 ${
                  loading
                    ? 'animate-spin'
                    : ''
                }`}
              />
            </button>
          </div>
        </div>


        <div className="px-4 sm:px-5 py-2.5 border-b border-slate-100 bg-indigo-50/40 text-[10px] text-indigo-700 flex items-center gap-2">
          <ShieldCheck className="w-3.5 h-3.5 shrink-0" />

          <span>
            All accessible filesystem drives are available. Windows permissions remain authoritative.
          </span>
        </div>


        <div className="flex-1 min-h-[260px] overflow-y-auto p-3 sm:p-4 bg-white">
          {deviceStatus !== 'online' ? (
            <div className="h-full min-h-[220px] flex flex-col items-center justify-center text-center px-6">
              <AlertTriangle className="w-8 h-8 text-amber-500 mb-3" />

              <h4 className="font-bold text-sm text-slate-800">
                Laptop Agent Offline
              </h4>

              <p className="text-xs text-slate-500 mt-1">
                Start KAIZEN Remote on the personal laptop before browsing folders.
              </p>
            </div>
          ) : loading && !result ? (
            <div className="h-full min-h-[220px] flex items-center justify-center">
              <div className="text-center">
                <Loader2 className="w-6 h-6 animate-spin text-indigo-600 mx-auto" />

                <p className="text-xs text-slate-500 mt-2">
                  Reading laptop folders...
                </p>
              </div>
            </div>
          ) : error && entries.length === 0 ? (
            <div className="min-h-[220px] flex flex-col items-center justify-center text-center px-6">
              <AlertTriangle className="w-7 h-7 text-rose-500 mb-2" />

              <p className="text-xs text-rose-700">
                {error}
              </p>

              <button
                type="button"
                onClick={() =>
                  void load(
                    currentPath
                  )
                }
                className="mt-3 px-4 py-2 rounded-lg bg-slate-100 text-xs font-semibold text-slate-700"
              >
                Try Again
              </button>
            </div>
          ) : entries.length === 0 ? (
            <div className="min-h-[220px] flex flex-col items-center justify-center text-center">
              <Folder className="w-8 h-8 text-slate-300 mb-2" />

              <p className="text-xs text-slate-500">
                No subfolders found.
              </p>

              {currentPath && (
                <p className="text-[11px] text-slate-400 mt-1">
                  You can still select this folder.
                </p>
              )}
            </div>
          ) : (
            <motion.div
              key={
                showingRoots
                  ? 'folder-roots'
                  : currentPath
              }
              initial={{
                opacity: 0,
                x: 10
              }}
              animate={{
                opacity: 1,
                x: 0
              }}
              transition={
                kaizenStateTransition
              }
              className="space-y-1.5"
            >

              {entries.map(
                (
                  entry,
                  index
                ) => (

                  <motion.button
                    type="button"
                    key={entry.path}
                    layout="position"
                    initial={{
                      opacity: 0,
                      y: 5
                    }}
                    animate={{
                      opacity: 1,
                      y: 0
                    }}
                    whileTap={{
                      scale: 0.985
                    }}
                    transition={{
                      ...kaizenStateTransition,
                      delay:
                        Math.min(
                          index * 0.018,
                          0.12
                        )
                    }}
                    disabled={loading}
                    onClick={() =>
                      void load(
                        entry.path
                      )
                    }
                    className="w-full min-h-[52px] px-3 py-2 rounded-xl border border-transparent hover:border-indigo-100 hover:bg-indigo-50/50 active:bg-indigo-50 flex items-center gap-3 text-left disabled:opacity-50"
                  >

                    <motion.div
                      whileTap={{
                        scale: 0.94
                      }}
                      transition={
                        kaizenStateTransition
                      }
                      className={`w-9 h-9 shrink-0 rounded-lg border flex items-center justify-center ${
                        entry.isRoot
                          ? 'bg-slate-900 border-slate-800 text-indigo-300'
                          : 'bg-indigo-50 border-indigo-100 text-indigo-600'
                      }`}
                    >

                      {entry.isRoot
                        ? (
                            <HardDrive className="w-4 h-4" />
                          )
                        : (
                            <Folder className="w-4 h-4" />
                          )}

                    </motion.div>


                    <div className="min-w-0 flex-1">

                      <span className="block text-xs font-semibold text-slate-800 truncate">
                        {entry.name}
                      </span>

                      {entry.isRoot && (

                        <span className="block text-[10px] font-mono text-slate-400 truncate mt-0.5">
                          {entry.path}
                        </span>

                      )}

                    </div>

                  </motion.button>

                )
              )}


              {result?.truncated && (

                <motion.div
                  initial={{
                    opacity: 0,
                    y: 4
                  }}
                  animate={{
                    opacity: 1,
                    y: 0
                  }}
                  transition={
                    kaizenStateTransition
                  }
                  className="p-2 text-[10px] text-amber-700 bg-amber-50 border border-amber-100 rounded-lg"
                >
                  This folder contains more than 500 subdirectories. Only the first 500 are shown.
                </motion.div>

              )}

            </motion.div>
          )}
        </div>


        <div className="shrink-0 px-4 sm:px-5 py-3 border-t border-slate-100 bg-slate-50 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
          {currentPath ? (
            <div className="space-y-2">
              <div className="text-[10px] text-slate-400 font-mono truncate">
                {currentPath}
              </div>

              <button
                type="button"
                disabled={loading}
                onClick={() =>
                  onSelect(
                    currentPath
                  )
                }
                className="w-full min-h-[44px] rounded-lg bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 disabled:bg-slate-300 text-white text-xs font-semibold flex items-center justify-center gap-2"
              >
                <FolderOpen className="w-4 h-4" />

                <span>
                  Select This Folder
                </span>
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={onClose}
              className="w-full min-h-[44px] rounded-lg bg-slate-100 text-slate-700 text-xs font-semibold"
            >
              Cancel
            </button>
          )}
        </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};